import { GridItem, Paper } from '../common/Form'
import Wrapper from '../common/Wrapper'
import { Link, useNavigate } from 'react-router'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import { AdInsertionSettingsForm } from './AdInsertionSettings'
import Grid from '@mui/material/Grid'
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import CardHeader from '@mui/material/CardHeader'
import { useAdBreaksSelector, useAdCreativeSelector, useAdPodsSelector, withDefaultPagination } from '../../utils'
import { useTheme } from '@mui/material/styles'
import Chip from '@mui/material/Chip'
import Skeleton from '@mui/material/Skeleton'
import Grow from '@mui/material/Grow'
import Stack from '@mui/material/Stack'
import LinearProgress from '@mui/material/LinearProgress'
import { AdBreak, AdBreakStatus, AdCreativeStatus, AdPodStatus } from 'common/api/v1/types'
import { DATE_FORMAT_LONG } from 'common/api/v1/helpers'
import { format } from 'date-fns'
import { AppDispatch, useRoutes } from '../../store'
import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { listAdBreaks } from '../../redux/actions/adBreakActions'
import { listAdPods } from '../../redux/actions/adPodActions'
import { listAdCreatives } from '../../redux/actions/adCreativeActions'
import { Api } from '../../store'
import { adBreakStatusText } from './adBreak/tableConfig'
import WarningIcon from '@mui/icons-material/WarningAmber'
import { getEndTime } from './adBreak/utils'

const stretchedPaper = {
  margin: 0,
  overflow: 'hidden',
  height: '100%',
  display: 'flex',
  justifyContent: 'space-between',
  flexDirection: 'column',
}

export const AdInsertionPage = () => {
  const navigate = useNavigate()

  const { adBreaks, total: totalAdBreaks } = useAdBreaksSelector(withDefaultPagination({}))
  const [failedAdBreaks, setFailedAdBreaks] = useState(0)

  const { total: totalAdPods } = useAdPodsSelector(withDefaultPagination({}))
  const [failedAdPods, setFailedAdPods] = useState(0)

  const { total: totalAdCreatives } = useAdCreativeSelector(withDefaultPagination({}))
  const [failedAdCreatives, setFailedAdCreatives] = useState(0)

  const dispatch = useDispatch<AppDispatch>()

  const refreshFailedAdBreaks = () => {
    Api.adBreaksApi
      .listAdBreaks({ status: AdBreakStatus.playFailed, pageNumber: '0', rowsPerPage: '1' })
      .then((result: any) => {
        const failed = result?.total ?? result?.payload?.total
        if (!failed) return
        setFailedAdBreaks(failed)
      })
      .catch(() => {
        setFailedAdBreaks(0)
      })
  }
  const refreshFailedAdPods = () => {
    Api.adPodsApi
      .listAdPods({ status: AdPodStatus.unhealthy, pageNumber: '0', rowsPerPage: '1' })
      .then((result: any) => {
        const failed = result?.total ?? result?.payload?.total
        if (!failed) return
        setFailedAdPods(failed)
      })
      .catch(() => {
        setFailedAdPods(0)
      })
  }
  const refreshFailedAdCreatives = () => {
    Api.adCreativesApi
      .listAdCreatives({ status: AdCreativeStatus.probeFailed, pageNumber: '0', rowsPerPage: '1' })
      .then((result: any) => {
        const failed = result?.total ?? result?.payload?.total
        if (!failed) return
        setFailedAdCreatives(failed)
      })
      .catch(() => {
        setFailedAdCreatives(0)
      })
  }

  // Slow fetch
  useEffect(() => {
    refreshFailedAdPods()
    dispatch(listAdPods(withDefaultPagination({})))

    refreshFailedAdCreatives()
    dispatch(listAdCreatives(withDefaultPagination({})))

    const interval = setInterval(() => {
      refreshFailedAdPods()
      dispatch(listAdPods(withDefaultPagination({})))

      refreshFailedAdCreatives()
      dispatch(listAdCreatives(withDefaultPagination({})))
    }, 5000)

    return () => {
      clearInterval(interval)
    }
  }, [dispatch])

  // Fast fetch of ad breaks
  useEffect(() => {
    refreshFailedAdBreaks()
    dispatch(listAdBreaks(withDefaultPagination({})))

    const interval = setInterval(() => {
      refreshFailedAdBreaks()
      dispatch(listAdBreaks(withDefaultPagination({})))
    }, 500)

    return () => {
      clearInterval(interval)
    }
  }, [dispatch])

  return (
    <Wrapper name="Ad Insertion">
      <Paper className="outlined" title="Monitor" sx={{ mb: 3 }}>
        <Grid size={{ sm: 12, md: 12 }}>
          <Grid container spacing={3} alignItems="stretch" pb={3}>
            <Grid size={{ sm: 12, md: 12 }}>
              <Paper sx={stretchedPaper} title="Ad Breaks">
                <GridItem lg={12} xl={12}></GridItem>

                <Box
                  sx={{
                    pb: 1,
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'row',
                    overflowX: 'auto',
                    flexWrap: 'nowrap',
                    gap: 2,
                  }}
                >
                  {adBreaks.map((adBreak) => (
                    <AdBreakCard adBreak={adBreak} key={adBreak.id} />
                  ))}
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body1">
                      {totalAdBreaks} Ad Break{totalAdBreaks === 1 ? '' : 's'} recorded{' '}
                    </Typography>
                    {failedAdBreaks > 0 ? (
                      <Chip
                        icon={<WarningIcon />}
                        color="warning"
                        size="small"
                        label={`${failedAdBreaks} failed`}
                        sx={{ mr: 1 }}
                      />
                    ) : undefined}
                  </Stack>

                  <Button variant="contained" onClick={() => navigate('adBreaks')}>
                    View breaks
                  </Button>
                </Box>
              </Paper>
            </Grid>

            <Grid size={{ sm: 12, md: 6 }}>
              <Paper sx={stretchedPaper} title="Audit Log">
                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                  <Typography variant="body1">Explore logs from all Ad Insertion services</Typography>
                  <Button variant="contained" onClick={() => navigate('adAudit')}>
                    View logs
                  </Button>
                </Box>
              </Paper>
            </Grid>

            <Grid size={{ sm: 12, md: 6 }}>
              <Paper sx={stretchedPaper} title={'Metrics'}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                  <Typography variant="body1">Explore metrics from all Ad Insertion services</Typography>
                  <Button
                    variant="contained"
                    href="/grafana/d/ad-insertion/ad-insertion-metrics?orgId=1&refresh=5s"
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    View metrics
                  </Button>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Grid>
      </Paper>

      <Paper className="outlined" title="Configure">
        <Grid size={{ sm: 12, md: 12 }}>
          <Grid container spacing={3} alignItems="stretch" pb={3}>
            <Grid size={{ sm: 12, md: 6 }}>
              <Paper sx={stretchedPaper} title="Creatives">
                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body1">
                      {totalAdCreatives} Creative{totalAdCreatives === 1 ? '' : 's'} available{' '}
                    </Typography>
                    {failedAdCreatives > 0 ? (
                      <Chip
                        icon={<WarningIcon />}
                        color="warning"
                        size="small"
                        label={`${failedAdCreatives} unhealthy`}
                        sx={{ mr: 1 }}
                      />
                    ) : undefined}
                  </Stack>
                  <Button variant="contained" onClick={() => navigate('adCreatives')}>
                    View creatives
                  </Button>
                </Box>
              </Paper>
            </Grid>

            <Grid size={{ sm: 12, md: 6 }}>
              <Paper sx={stretchedPaper} title="Pods">
                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body1">
                      {totalAdPods} Pod{totalAdPods === 1 ? '' : 's'} available{' '}
                    </Typography>
                    {failedAdPods > 0 ? (
                      <Chip
                        icon={<WarningIcon />}
                        color="warning"
                        size="small"
                        label={`${failedAdPods} unhealthy`}
                        sx={{ mr: 1 }}
                      />
                    ) : undefined}
                  </Stack>
                  <Button variant="contained" onClick={() => navigate('adPods')}>
                    View pods
                  </Button>
                </Box>
              </Paper>
            </Grid>

            <Grid size={{ sm: 12, md: 12 }}>
              <AdInsertionSettingsForm />
            </Grid>
          </Grid>
        </Grid>
      </Paper>
    </Wrapper>
  )
}

const AdBreakCard = ({ adBreak }: { adBreak: AdBreak }) => {
  const routes = useRoutes()
  const theme = useTheme()

  const isCreativeFiller = (creativeId: string) => {
    return adBreak.playlist?.ads?.some((ad) => ad.id.endsWith(creativeId) && ad.filler)
  }

  const notFillerAdsTrackingEvents = adBreak.trackingEvents.filter((e) => !isCreativeFiller(e.adCreativeId))
  const reportedTrackingEvents = notFillerAdsTrackingEvents.filter((e) => e.reportCount > 0)

  const progress = adBreak?.playlist?.ads
    ? (reportedTrackingEvents.length / notFillerAdsTrackingEvents.length) * 100
    : undefined

  const statusIsPlaying = [AdBreakStatus.playing, AdBreakStatus.pending, AdBreakStatus.collectingStats].includes(
    adBreak.status,
  )

  const now = new Date()
  const endTime = getEndTime(adBreak).timestamp
  const hasEndedWithBuffer = endTime && now.getTime() > endTime.getTime() + 30_000

  const progressBarColor = () => {
    if (hasEndedWithBuffer && !progress) {
      return 'error'
    }

    if (statusIsPlaying || !progress) {
      return 'primary'
    }

    if (progress > 99) {
      return 'success'
    }

    return 'error'
  }

  return (
    <Grow in={true} timeout={200}>
      <Link to={`${routes.adBreaks()}/view/${adBreak.id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
        <Card
          sx={{
            whiteSpace: 'nowrap',
            flexShrink: 0,
            borderColor: theme.palette.grey[800],
            borderWidth: 2,
            borderStyle: 'solid',
            minWidth: 250,
          }}
          key={adBreak.id}
        >
          <CardHeader
            title={format(adBreak.startsAt, DATE_FORMAT_LONG)}
            subheader={
              adBreak?.playlist?.ads && (
                <>
                  {adBreak.playlist.ads.length} ad{adBreak.playlist.ads.length === 1 ? '' : 's'},{' '}
                  {adBreak.playlist.ads.length
                    ? adBreak.playlist.ads
                        .map((a) => a.durationMs / 1000)
                        .reduce((p, c) => p + c)
                        .toFixed(2)
                    : 0}{' '}
                  seconds
                </>
              )
            }
          />
          <CardContent>
            <Stack direction="column" spacing={1}>
              {adBreak.trackingEvents.length ? (
                <>
                  <LinearProgress
                    value={progress}
                    variant={
                      hasEndedWithBuffer
                        ? 'determinate'
                        : statusIsPlaying || !progress
                          ? 'indeterminate'
                          : 'determinate'
                    }
                    color={progressBarColor()}
                  />
                  <Typography variant="body2">
                    {hasEndedWithBuffer && !progress ? (
                      0
                    ) : progress ? (
                      `${progress.toFixed(0)}`
                    ) : (
                      <Skeleton width={'1em'} sx={{ display: 'inline-block', mr: 0.5 }} />
                    )}
                    %
                  </Typography>
                </>
              ) : null}
              <Typography variant="body2">{adBreakStatusText(adBreak.status, adBreak.errorMessage)}</Typography>
            </Stack>
          </CardContent>
        </Card>
      </Link>
    </Grow>
  )
}
